/**
 * 指定時間外診療予約データのマッピング処理
 * CSVデータから61項目の表示用データへのマッピング
 */

import type { MedicalReservationRecord, OutOfHoursDisplayRecord } from '../types/medicalReservation';

/**
 * CSVデータから表示用61項目へのマッピング
 * @param record - 診療予約レコード
 * @returns 61項目の表示用データを含むレコード
 */
export const mapToOutOfHoursDisplay = (record: MedicalReservationRecord): OutOfHoursDisplayRecord => {
  // 基本レコードデータをコピー
  const outOfHoursRecord: OutOfHoursDisplayRecord = {
    ...record,
    displayData: {
      '成果報酬': '',
      '自動プログラム使用': '',
      '担当カウンセラー': '',
      'カウンセラー対応': '',
      '担当医師': '',
      'anybot ID': '',
      '診療開始時間': '',
      'tags': '',
      'ステータス': '',
      '会員登録': '',
      '問診回答': '',
      '再診用問診': '',
      '登録電話番号': '',
      '診療可能な電話番号': '',
      '使用ツール': '',
      '名前（漢字）': '',
      '名前（カナ）': '',
      '遅延': '',
      '診療対応': '',
      '低用量ピル': '',
      '中用量ピル': '',
      'アフターピル': '',
      '副作用緩和薬': '',
      'バイク便': '',
      'CS対応内容': '',
      '処方チェック': '',
      'mederi CSから医師へ': '',
      '処方可能医薬品': '',
      '年齢': '',
      '親への同意': '',
      '服用経験': '',
      '服用していたピル': '',
      '改善目的': '',
      '希望ピル': '',
      '医師に聞きたいこと': '',
      '再診身分証': '',
      '再診予約理由': '',
      '再診相談内容詳細': '',
      '前回生理開始日': '',
      '次回生理日': '',
      '生理を避けたい日にち': '',
      '性行為の日時': '',
      '妊娠希望': '',
      '妊娠経験': '',
      '妊娠時期・出産回数': '',
      '妊娠・出産・授乳有無': '',
      '過去妊娠中の既往歴': '',
      '手術歴': '',
      '片頭痛': '',
      '既往歴': '',
      'アレルギー': '',
      '身長': '',
      '体重': '',
      'BMI': '',
      '高血圧診断': '',
      'タバコ': '',
      '不正出血': '',
      '婦人科系疾患の既往歴': '',
      'その他疾患の既往歴': '',
      'ご家族に血栓症': '',
      'ご家族に乳がん': '',
      '身分証': '',
      '採血画像': ''
    }
  };

  // 既存のプロパティから直接マッピング可能なもの
  outOfHoursRecord.displayData['担当医師'] = record.doctorInCharge || '';
  outOfHoursRecord.displayData['anybot ID'] = record.anybotId || '';
  outOfHoursRecord.displayData['名前（漢字）'] = record.patientName || '';
  outOfHoursRecord.displayData['診療開始時間'] = record.consultationStartTime || '';
  outOfHoursRecord.displayData['ステータス'] = record.actionStatus || '';

  // csvDataからのマッピング
  if (record.csvData) {
    // 基本マッピング
    outOfHoursRecord.displayData['成果報酬'] = record.csvData['成果報酬'] || '';
    outOfHoursRecord.displayData['自動プログラム使用'] = record.csvData['自動プログラム使用'] || '';
    outOfHoursRecord.displayData['担当カウンセラー'] = record.csvData['担当カウンセラー'] || '';
    outOfHoursRecord.displayData['カウンセラー対応'] = record.csvData['カウンセラー対応'] || '';
    outOfHoursRecord.displayData['tags'] = record.csvData['tags'] || '';
    outOfHoursRecord.displayData['会員登録'] = record.csvData['会員登録'] || '';
    outOfHoursRecord.displayData['問診回答'] = record.csvData['問診回答'] || '';
    outOfHoursRecord.displayData['再診用問診'] = record.csvData['再診用問診'] || '';

    // 電話番号関連
    outOfHoursRecord.displayData['登録電話番号'] = record.csvData['登録電話番号'] || '';
    outOfHoursRecord.displayData['診療可能な電話番号'] = record.csvData['診療可能な電話番号'] || '';

    // ツール関連
    outOfHoursRecord.displayData['使用ツール'] = record.csvData['使用ツール'] || record.csvData['診療スタイル'] || '';

    // 名前関連
    outOfHoursRecord.displayData['名前（カナ）'] = record.csvData['名前（カナ）'] || '';

    // 診療関連
    outOfHoursRecord.displayData['遅延'] = record.csvData['遅延'] || record.csvData['診療リクエスト'] || '';
    outOfHoursRecord.displayData['診療対応'] = record.csvData['診療対応'] || '';

    // ピル・薬剤関連
    outOfHoursRecord.displayData['低用量ピル'] = record.csvData['低用量ピル'] || '';
    outOfHoursRecord.displayData['中用量ピル'] = record.csvData['中用量処方有無'] || '';
    outOfHoursRecord.displayData['アフターピル'] = record.csvData['アフターピル処方有無'] || '';
    outOfHoursRecord.displayData['副作用緩和薬'] = record.csvData['副作用改善薬処方有無'] || '';

    // CS関連
    outOfHoursRecord.displayData['バイク便'] = record.csvData['CS対応必要有無'] || '';
    outOfHoursRecord.displayData['CS対応内容'] = record.csvData['医師からCS/カウンセラー'] || '';
    outOfHoursRecord.displayData['処方チェック'] = record.csvData['Wチェック'] || '';
    outOfHoursRecord.displayData['mederi CSから医師へ'] = record.csvData['mederi CSから医師へ'] || '';
    outOfHoursRecord.displayData['処方可能医薬品'] = record.csvData['カウンセラーから医師へ'] || '';

    // 患者情報
    outOfHoursRecord.displayData['年齢'] = record.csvData['年齢'] || '';
    outOfHoursRecord.displayData['親への同意'] = record.csvData['親への同意'] || '';
    outOfHoursRecord.displayData['服用経験'] = record.csvData['服用経験'] || '';
    outOfHoursRecord.displayData['服用していたピル'] = record.csvData['服用していたピル'] || '';
    outOfHoursRecord.displayData['改善目的'] = record.csvData['改善目的'] || '';
    outOfHoursRecord.displayData['希望ピル'] = record.csvData['希望ピル'] || '';
    outOfHoursRecord.displayData['医師に聞きたいこと'] = record.csvData['医師に聞きたいこと'] || '';

    // 再診問診関連
    outOfHoursRecord.displayData['再診身分証'] = record.csvData['※再診問診 身分証'] || '';
    outOfHoursRecord.displayData['再診予約理由'] = record.csvData['※再診問診 予約理由'] || '';
    outOfHoursRecord.displayData['再診相談内容詳細'] = record.csvData['※再診問診 相談内容詳細'] || '';

    // 生理・妊娠関連
    outOfHoursRecord.displayData['前回生理開始日'] = record.csvData['前回生理開始日'] || '';
    outOfHoursRecord.displayData['次回生理日'] = record.csvData['月経周期'] || '';
    outOfHoursRecord.displayData['生理を避けたい日にち'] = record.csvData['生理を避けたい日にち'] || '';
    outOfHoursRecord.displayData['性行為の日時'] = record.csvData['性行為の日時'] || '';
    outOfHoursRecord.displayData['妊娠希望'] = record.csvData['妊娠希望'] || '';
    outOfHoursRecord.displayData['妊娠経験'] = record.csvData['妊娠経験'] || '';
    outOfHoursRecord.displayData['妊娠時期・出産回数'] = record.csvData['妊娠時期・出産回数'] || '';
    outOfHoursRecord.displayData['妊娠・出産・授乳有無'] = record.csvData['妊娠・出産・授乳有無'] || '';

    // 既往歴関連
    outOfHoursRecord.displayData['過去妊娠中の既往歴'] = record.csvData['過去妊娠中に「黄疸」「ヘルペス」「持続性掻疼症」と診断されたことはありますか？'] || '';
    outOfHoursRecord.displayData['手術歴'] = record.csvData['過去2週間以内に手術'] || '';
    outOfHoursRecord.displayData['片頭痛'] = record.csvData['前兆のある片頭痛'] || '';
    outOfHoursRecord.displayData['既往歴'] = record.csvData['飲んでいるお薬'] || '';

    // アレルギー・身体情報
    outOfHoursRecord.displayData['アレルギー'] = record.csvData['アレルギー'] || '';
    outOfHoursRecord.displayData['身長'] = record.csvData['身長'] || '';
    outOfHoursRecord.displayData['体重'] = record.csvData['体重'] || '';
    outOfHoursRecord.displayData['BMI'] = record.csvData['BMIの値は30以下'] || '';
    outOfHoursRecord.displayData['高血圧診断'] = record.csvData['高血圧診断'] || '';
    outOfHoursRecord.displayData['タバコ'] = record.csvData['タバコ'] || '';

    // 症状・疾患
    outOfHoursRecord.displayData['不正出血'] = record.csvData['不正出血'] || '';
    outOfHoursRecord.displayData['婦人科系疾患の既往歴'] = record.csvData['婦人科系疾患'] || '';
    outOfHoursRecord.displayData['その他疾患の既往歴'] = record.csvData['その他疾患'] || '';
    outOfHoursRecord.displayData['ご家族に血栓症'] = record.csvData['ご家族に血栓症'] || '';
    outOfHoursRecord.displayData['ご家族に乳がん'] = record.csvData['ご家族に乳がん'] || '';

    // 書類
    outOfHoursRecord.displayData['身分証'] = record.csvData['身分証'] || '';
    outOfHoursRecord.displayData['採血画像'] = record.csvData['採血画像'] || '';
  }

  return outOfHoursRecord;
};

/**
 * 複数レコードの一括マッピング
 * @param records - 診療予約レコード配列
 * @returns 61項目の表示用データを含むレコード配列
 */
export const mapToOutOfHoursDisplayBulk = (records: MedicalReservationRecord[]): OutOfHoursDisplayRecord[] => {
  return records.map(record => mapToOutOfHoursDisplay(record));
};

/**
 * 表示用ヘッダーラベルを取得（改行を含む）
 * @param key - フィールドキー
 * @returns 表示用ラベル
 */
export const getDisplayLabel = (key: string): string => {
  const labelMap: { [key: string]: string } = {
    '成果報酬': '成果報酬',
    '自動プログラム使用': '自動プログラム使用',
    '担当カウンセラー': '担当カウンセラー',
    'カウンセラー対応': 'カウンセラー対応',
    '担当医師': '担当医師',
    'anybot ID': 'anybot ID',
    '診療開始時間': '診療開始時間',
    'tags': 'tags',
    'ステータス': 'ステータス',
    '会員登録': '会員登録',
    '問診回答': '問診回答',
    '再診用問診': '再診用問診',
    '登録電話番号': '登録電話番号',
    '診療可能な電話番号': '診療可能な電話番号',
    '使用ツール': '使用ツール',
    '名前（漢字）': '名前（漢字）',
    '名前（カナ）': '名前（カナ）',
    '遅延': '遅延',
    '診療対応': '診療対応',
    '低用量ピル': '低用量ピル',
    '中用量ピル': '中用量ピル',
    'アフターピル': 'アフターピル',
    '副作用緩和薬': '副作用緩和薬',
    'バイク便': 'バイク便',
    'CS対応内容': 'CS対応内容/医師からCSへ',
    '処方チェック': '処方チェック',
    'mederi CSから医師へ': 'mederi CSから医師へ',
    '処方可能医薬品': '処方可能医薬品',
    '年齢': '年齢',
    '親への同意': '親への同意',
    '服用経験': '服用経験',
    '服用していたピル': '服用していたピル',
    '改善目的': '改善目的',
    '希望ピル': '希望ピル',
    '医師に聞きたいこと': '医師に聞きたいこと',
    '再診身分証': '再診身分証',
    '再診予約理由': '再診予約理由',
    '再診相談内容詳細': '再診相談内容詳細',
    '前回生理開始日': '前回生理開始日',
    '次回生理日': '次回生理日',
    '生理を避けたい日にち': '生理を避けたい日にち',
    '性行為の日時': '性行為の日時',
    '妊娠希望': '妊娠希望（3ヶ月以内）',
    '妊娠経験': '妊娠経験',
    '妊娠時期・出産回数': '妊娠時期・出産回数',
    '妊娠・出産・授乳有無': '妊娠・出産・授乳有無',
    '過去妊娠中の既往歴': '過去妊娠中の既往歴',
    '手術歴': '手術歴',
    '片頭痛': '片頭痛',
    '既往歴': '既往歴',
    'アレルギー': 'アレルギー',
    '身長': '身長',
    '体重': '体重',
    'BMI': 'BMI',
    '高血圧診断': '高血圧診断',
    'タバコ': 'タバコ',
    '不正出血': '不正出血',
    '婦人科系疾患の既往歴': '婦人科系疾患の既往歴',
    'その他疾患の既往歴': 'その他疾患の既往歴',
    'ご家族に血栓症': 'ご家族に血栓症（50歳未満）',
    'ご家族に乳がん': 'ご家族に乳がん',
    '身分証': '身分証',
    '採血画像': '採血画像'
  };

  return labelMap[key] || key;
};